import type { ImagePickerAsset } from 'expo-image-picker';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  type CollectionReference,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  uploadBytesResumable,
  type UploadMetadata,
  type UploadResult,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import { db, storage } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import {
  blobFromMediaAsset,
  MAX_PHOTO_UPLOAD_SOURCE_BYTES,
  prepareImageUpload,
} from '@/src/features/media/image-upload';
import {
  MAX_POST_VIDEO_BYTES,
  MAX_VIDEO_DURATION_MILLIS,
  isReaction,
  type CommentMedia,
  type CommentUploadMediaKind,
  type ContentEditHistoryEntry,
  type MediaThumbnail,
  parseReactionCounts,
  totalReactionCounts,
  type LinkPreview,
  type MediaThumbnailStatus,
  type ProfileMention,
  type Reaction,
  type ReactionActor,
  type ReactionCounts,
} from '@/src/features/posts/post-service';
import {
  getProfileAvatarURL,
  getPublicProfile,
} from '@/src/features/profile/profile-service';
import { withLiveGarageVehicleOwnerProfile } from '@/src/features/garage/garage-owner-profile';
import { fetchPublicJson } from '@/src/utils/public-api';
import { UserFacingError } from '@/src/utils/errors';

export const GARAGE_PHOTO_LIMIT = 12;
export const MAX_GARAGE_PHOTO_BYTES = MAX_PHOTO_UPLOAD_SOURCE_BYTES;
export const MAX_GARAGE_STORY_LENGTH = 4000;
export const MAX_GARAGE_MODS = 30;
export const MAX_GARAGE_COMMENT_LENGTH = 1000;

export const VEHICLE_TYPES = [
  'car',
  'truck',
  'suv',
  'van',
  'motorcycle',
  'off_road',
] as const;
export const VEHICLE_OWNERSHIP_STATUSES = [
  'current',
  'past',
  'project',
] as const;

export type FirestoreDate = Timestamp | Date | string | null;
type GarageMediaThumbnail = MediaThumbnail;
export type VehicleType = (typeof VEHICLE_TYPES)[number];
export type VehicleOwnershipStatus =
  (typeof VEHICLE_OWNERSHIP_STATUSES)[number];

export type GarageMod = {
  id: string;
  category: string | null;
  title: string;
  brandPart: string | null;
  notes: string | null;
};

export type GarageVehiclePhoto = {
  id: string;
  contentType: string;
  downloadURL: string;
  height: number;
  isCover: boolean;
  order: number;
  sizeBytes: number;
  storagePath: string;
  thumbnail: GarageMediaThumbnail | null;
  thumbnailError?: string | null;
  thumbnailStatus: MediaThumbnailStatus | null;
  uploadedAt: FirestoreDate;
  width: number;
};

export type GarageVehicle = {
  id: string;
  ownerUid: string;
  ownerFullName: string | null;
  ownerPhotoURL: string | null;
  ownerUsername: string | null;
  ownerFounderBadge?: boolean;
  ownerVerifiedBadge?: boolean;
  slug: string;
  type: VehicleType;
  ownershipStatus: VehicleOwnershipStatus;
  nickname: string | null;
  year: number | null;
  make: string;
  model: string;
  trim: string | null;
  story: string | null;
  mods: GarageMod[];
  photoCount: number;
  coverPhoto: GarageVehiclePhoto | null;
  reactionCounts: ReactionCounts;
  reactionCount: number;
  commentCount: number;
  createdAt: FirestoreDate;
  updatedAt: FirestoreDate;
};

export type GarageVehicleComment = {
  id: string;
  authorFounderBadge?: boolean;
  authorUid: string;
  authorFullName: string | null;
  authorUsername: string | null;
  authorPhotoURL: string | null;
  authorVerifiedBadge?: boolean;
  body: string;
  media: CommentMedia | null;
  linkPreview: LinkPreview | null;
  parentCommentId: string | null;
  profileMentions: ProfileMention[];
  reactionCounts: ReactionCounts;
  reactionCount: number;
  replyToCommentId: string | null;
  replyToFullName: string | null;
  replyToUid: string | null;
  replyToUsername: string | null;
  createdAt: FirestoreDate;
  editCount: number;
  editedAt: FirestoreDate;
  localCreatedAtMillis?: number;
  syncStatus?: 'pending' | 'uploading' | 'synced' | 'failed';
  updatedAt: FirestoreDate;
};

export type CreateGarageVehicleCommentInput = {
  authorUid: string;
  body: string;
  giphyGif?: GiphyGif | null;
  mediaAsset?: ImagePickerAsset | null;
  mediaKind?: CommentUploadMediaKind | null;
  onUploadProgress?: (progress: number) => void;
  parentCommentId?: string | null;
  replyToCommentId?: string | null;
  profileMentions?: ProfileMention[];
  vehicleId: string;
};

export type VehicleCatalogOption = {
  label: string;
  value: string;
};

export type VehiclePhotoUpload = {
  clientId: string;
  contentType: string;
  downloadURL: string;
  height: number;
  order: number;
  sizeBytes: number;
  storagePath: string;
  width: number;
};

export type GarageVehicleFormInput = {
  vehicleId?: string;
  coverPhotoId?: string | null;
  make: string;
  model: string;
  mods: GarageMod[];
  nickname?: string | null;
  ownershipStatus: VehicleOwnershipStatus;
  photos: VehiclePhotoUpload[];
  story?: string | null;
  trim?: string | null;
  type: VehicleType;
  year?: number | null;
};

function isVehicleType(value: unknown): value is VehicleType {
  return VEHICLE_TYPES.includes(value as VehicleType);
}

function isVehicleOwnershipStatus(
  value: unknown,
): value is VehicleOwnershipStatus {
  return VEHICLE_OWNERSHIP_STATUSES.includes(value as VehicleOwnershipStatus);
}

function normalizeString(value: unknown, maxLength = 2048) {
  return typeof value === 'string' && value.trim()
    ? value.trim().slice(0, maxLength)
    : null;
}

function finitePositiveInteger(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

function parseMediaThumbnail(value: unknown): GarageMediaThumbnail | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (
    typeof data.storagePath !== 'string' ||
    typeof data.downloadURL !== 'string' ||
    data.contentType !== 'image/jpeg'
  ) {
    return null;
  }

  return {
    storagePath: data.storagePath,
    downloadURL: data.downloadURL,
    contentType: 'image/jpeg',
    sizeBytes: finitePositiveInteger(data.sizeBytes),
    width: finitePositiveInteger(data.width),
    height: finitePositiveInteger(data.height),
    generatedAt:
      data.generatedAt instanceof Timestamp || data.generatedAt instanceof Date
        ? data.generatedAt
        : null,
  };
}

function parseThumbnailStatus(value: unknown): MediaThumbnailStatus | null {
  return value === 'queued' ||
    value === 'processing' ||
    value === 'ready' ||
    value === 'failed'
    ? value
    : null;
}

function parseGarageLinkPreview(value: unknown): LinkPreview | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (data.status !== 'ready' && data.status !== 'failed') {
    return null;
  }

  const url = normalizeString(data.url);
  const displayUrl = normalizeString(data.displayUrl, 240);

  if (!url || !displayUrl) {
    return null;
  }

  return {
    status: data.status,
    url,
    displayUrl,
    title: normalizeString(data.title, 160),
    description: normalizeString(data.description, 240),
    imageURL: normalizeString(data.imageURL),
    imageWidth:
      typeof data.imageWidth === 'number' &&
      Number.isFinite(data.imageWidth) &&
      data.imageWidth > 0
        ? Math.floor(data.imageWidth)
        : null,
    imageHeight:
      typeof data.imageHeight === 'number' &&
      Number.isFinite(data.imageHeight) &&
      data.imageHeight > 0
        ? Math.floor(data.imageHeight)
        : null,
    siteName: normalizeString(data.siteName, 120),
    fetchedAt: (data.fetchedAt as FirestoreDate) ?? null,
  };
}

function parseGarageCommentMedia(media: unknown): CommentMedia | null {
  if (!media || typeof media !== 'object') {
    return null;
  }

  const data = media as Record<string, unknown>;

  if (
    data.source === 'upload' &&
    (data.kind === 'image' || data.kind === 'video')
  ) {
    return {
      kind: data.kind,
      source: 'upload',
      storagePath: normalizeString(data.storagePath) ?? '',
      downloadURL: normalizeString(data.downloadURL) ?? '',
      contentType: normalizeString(data.contentType, 200) ?? '',
      sizeBytes: finitePositiveInteger(data.sizeBytes),
      width: finitePositiveInteger(data.width),
      height: finitePositiveInteger(data.height),
      durationMillis:
        typeof data.durationMillis === 'number' &&
        Number.isFinite(data.durationMillis)
          ? Math.max(0, Math.floor(data.durationMillis))
          : null,
      thumbnail: parseMediaThumbnail(data.thumbnail),
      thumbnailStatus: parseThumbnailStatus(data.thumbnailStatus),
      thumbnailError: normalizeString(data.thumbnailError, 500),
    };
  }

  if (data.source === 'giphy' && data.kind === 'gif') {
    return {
      kind: 'gif',
      source: 'giphy',
      giphyId: normalizeString(data.giphyId, 200) ?? '',
      title: normalizeString(data.title, 200) ?? 'GIPHY GIF',
      pageURL: normalizeString(data.pageURL) ?? '',
      previewURL: normalizeString(data.previewURL) ?? '',
      originalURL: normalizeString(data.originalURL) ?? '',
      width: finitePositiveInteger(data.width),
      height: finitePositiveInteger(data.height),
    };
  }

  return null;
}

function parseGaragePhoto(
  id: string,
  value: unknown,
): GarageVehiclePhoto | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const storagePath = normalizeString(data.storagePath);
  const downloadURL = normalizeString(data.downloadURL);

  if (!storagePath || !downloadURL) {
    return null;
  }

  return {
    id,
    contentType: normalizeString(data.contentType, 200) ?? 'image/jpeg',
    downloadURL,
    height: finitePositiveInteger(data.height),
    isCover: data.isCover === true,
    order:
      typeof data.order === 'number' && Number.isFinite(data.order)
        ? Math.max(0, Math.floor(data.order))
        : 0,
    sizeBytes: finitePositiveInteger(data.sizeBytes),
    storagePath,
    thumbnail: parseMediaThumbnail(data.thumbnail),
    thumbnailError: normalizeString(data.thumbnailError, 500),
    thumbnailStatus: parseThumbnailStatus(data.thumbnailStatus),
    uploadedAt: (data.uploadedAt as FirestoreDate) ?? null,
    width: finitePositiveInteger(data.width),
  };
}

function parseGarageMods(value: unknown): GarageMod[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const data = entry as Record<string, unknown>;
      const title = normalizeString(data.title, 120);

      if (!title) {
        return null;
      }

      return {
        id: normalizeString(data.id, 80) ?? `mod-${index}`,
        category: normalizeString(data.category, 80),
        title,
        brandPart: normalizeString(data.brandPart, 120),
        notes: normalizeString(data.notes, 500),
      };
    })
    .filter((entry): entry is GarageMod => entry !== null)
    .slice(0, MAX_GARAGE_MODS);
}

function parseProfileMentions(value: unknown): ProfileMention[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const data = entry as Record<string, unknown>;
      const uid = normalizeString(data.uid, 200);
      const label = normalizeString(data.label, 120);

      return uid && label ? { uid, label } : null;
    })
    .filter((entry): entry is ProfileMention => entry !== null);
}

function sanitizeProfileMentions(
  body: string,
  mentions: ProfileMention[] | undefined,
) {
  const seen = new Set<string>();
  const validMentions: ProfileMention[] = [];

  mentions?.forEach((mention) => {
    const uid = mention.uid.trim();
    const label = mention.label.trim();
    const key = `${uid}:${label}`;

    if (
      !uid ||
      !label ||
      label.length > 120 ||
      !body.includes(label) ||
      seen.has(key) ||
      validMentions.length >= 20
    ) {
      return;
    }

    seen.add(key);
    validMentions.push({ uid, label });
  });

  return validMentions;
}

function parseGarageVehicle(
  id: string,
  data: Record<string, unknown>,
): GarageVehicle {
  const reactionCounts = parseReactionCounts(data.reactionCounts);

  return {
    id,
    ownerUid: normalizeString(data.ownerUid, 200) ?? '',
    ownerFullName: normalizeString(data.ownerFullName, 80),
    ownerPhotoURL: normalizeString(data.ownerPhotoURL),
    ownerUsername: normalizeString(data.ownerUsername, 30),
    ownerFounderBadge: data.ownerFounderBadge === true,
    ownerVerifiedBadge: data.ownerVerifiedBadge === true,
    slug: normalizeString(data.slug, 80) ?? id,
    type: isVehicleType(data.type) ? data.type : 'car',
    ownershipStatus: isVehicleOwnershipStatus(data.ownershipStatus)
      ? data.ownershipStatus
      : 'current',
    nickname: normalizeString(data.nickname, 80),
    year:
      typeof data.year === 'number' && Number.isFinite(data.year)
        ? Math.floor(data.year)
        : null,
    make: normalizeString(data.make, 80) ?? 'Vehicle',
    model: normalizeString(data.model, 120) ?? '',
    trim: normalizeString(data.trim, 120),
    story: normalizeString(data.story, MAX_GARAGE_STORY_LENGTH),
    mods: parseGarageMods(data.mods),
    photoCount: finitePositiveInteger(data.photoCount),
    coverPhoto: parseGaragePhoto('cover', data.coverPhoto),
    reactionCounts,
    reactionCount:
      typeof data.reactionCount === 'number'
        ? Math.max(0, Math.floor(data.reactionCount))
        : totalReactionCounts(reactionCounts),
    commentCount: finitePositiveInteger(data.commentCount),
    createdAt: (data.createdAt as FirestoreDate) ?? null,
    updatedAt: (data.updatedAt as FirestoreDate) ?? null,
  };
}

function parseGarageVehicleSnapshot(snapshot: QuerySnapshot): GarageVehicle[] {
  return snapshot.docs.map((vehicleSnapshot) =>
    parseGarageVehicle(vehicleSnapshot.id, vehicleSnapshot.data()),
  );
}

function parseGarageComment(
  id: string,
  data: Record<string, unknown>,
): GarageVehicleComment {
  const reactionCounts = parseReactionCounts(data.reactionCounts);

  return {
    id,
    authorFounderBadge: data.authorFounderBadge === true,
    authorUid: normalizeString(data.authorUid, 200) ?? '',
    authorFullName:
      normalizeString(data.authorFullName, 80) ??
      normalizeString(data.authorDisplayName, 80),
    authorUsername: normalizeString(data.authorUsername, 30),
    authorPhotoURL: normalizeString(data.authorPhotoURL),
    authorVerifiedBadge: data.authorVerifiedBadge === true,
    body: normalizeString(data.body, MAX_GARAGE_COMMENT_LENGTH) ?? '',
    media: parseGarageCommentMedia(data.media),
    linkPreview: parseGarageLinkPreview(data.linkPreview),
    parentCommentId: normalizeString(data.parentCommentId, 120),
    profileMentions: parseProfileMentions(data.profileMentions),
    reactionCounts,
    reactionCount:
      typeof data.reactionCount === 'number'
        ? Math.max(0, Math.floor(data.reactionCount))
        : totalReactionCounts(reactionCounts),
    replyToCommentId: normalizeString(data.replyToCommentId, 120),
    replyToFullName: normalizeString(data.replyToFullName, 80),
    replyToUid: normalizeString(data.replyToUid, 200),
    replyToUsername: normalizeString(data.replyToUsername, 30),
    createdAt: (data.createdAt as FirestoreDate) ?? null,
    editCount:
      typeof data.editCount === 'number' && Number.isFinite(data.editCount)
        ? Math.max(0, Math.floor(data.editCount))
        : 0,
    editedAt: (data.editedAt as ContentEditHistoryEntry['editedAt']) ?? null,
    updatedAt: (data.updatedAt as FirestoreDate) ?? null,
  };
}

function parseGarageCommentEditHistory(
  id: string,
  data: Record<string, unknown>,
): ContentEditHistoryEntry {
  return {
    id,
    editNumber:
      typeof data.editNumber === 'number' && Number.isFinite(data.editNumber)
        ? Math.max(0, Math.floor(data.editNumber))
        : 0,
    editedAt: (data.editedAt as ContentEditHistoryEntry['editedAt']) ?? null,
    editorUid: normalizeString(data.editorUid, 200) ?? '',
    nextBody: normalizeString(data.nextBody, MAX_GARAGE_COMMENT_LENGTH),
    nextProfileMentions: parseProfileMentions(data.nextProfileMentions),
    previousBody: normalizeString(data.previousBody, MAX_GARAGE_COMMENT_LENGTH),
    previousProfileMentions: parseProfileMentions(data.previousProfileMentions),
  };
}

function parseGarageCommentSnapshot(
  snapshot: QuerySnapshot,
): GarageVehicleComment[] {
  return snapshot.docs.map((commentSnapshot) =>
    parseGarageComment(commentSnapshot.id, commentSnapshot.data()),
  );
}

function garageCommentCreatedAtMillis(comment: GarageVehicleComment) {
  if (!comment.createdAt) {
    return 0;
  }

  if (comment.createdAt instanceof Date) {
    return comment.createdAt.getTime();
  }

  if (
    comment.createdAt &&
    typeof comment.createdAt === 'object' &&
    'toMillis' in comment.createdAt &&
    typeof comment.createdAt.toMillis === 'function'
  ) {
    return comment.createdAt.toMillis();
  }

  if (typeof comment.createdAt !== 'string') {
    return 0;
  }

  const date = new Date(comment.createdAt);

  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function sortGarageCommentsNewestFirst(comments: GarageVehicleComment[]) {
  return [...comments].sort(
    (firstComment, secondComment) =>
      garageCommentCreatedAtMillis(secondComment) -
      garageCommentCreatedAtMillis(firstComment),
  );
}

function getGarageFileName(asset: ImagePickerAsset) {
  return (asset.fileName ?? 'vehicle-photo.jpg').replace(
    /[^a-zA-Z0-9._-]/g,
    '_',
  );
}

function getGarageCommentFileName(
  asset: ImagePickerAsset,
  kind: CommentUploadMediaKind,
) {
  const fallbackExtension = kind === 'image' ? 'jpg' : 'mp4';
  const fallbackName = `comment-media.${fallbackExtension}`;

  return (asset.fileName ?? fallbackName).replace(/[^a-zA-Z0-9._-]/g, '_');
}

function getGarageCommentContentType(
  asset: ImagePickerAsset,
  kind: CommentUploadMediaKind,
) {
  if (asset.mimeType) {
    return asset.mimeType;
  }

  return kind === 'image' ? 'image/jpeg' : 'video/mp4';
}

function normalizeGarageCommentMediaDimension(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0;
}

function normalizeGarageCommentVideoDuration(asset: ImagePickerAsset) {
  return typeof asset.duration === 'number' &&
    Number.isFinite(asset.duration) &&
    asset.duration >= 0
    ? Math.round(asset.duration)
    : null;
}

function validateGarageCommentMediaSize(
  sizeBytes: number,
  kind: CommentUploadMediaKind,
) {
  const maxSize =
    kind === 'image' ? MAX_GARAGE_PHOTO_BYTES : MAX_POST_VIDEO_BYTES;

  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    throw new UserFacingError(
      kind === 'image'
        ? 'We could not read that photo. Please choose a different image.'
        : 'We could not read that video. Please choose it again and retry.',
    );
  }

  if (sizeBytes > maxSize) {
    throw new Error(
      kind === 'image'
        ? 'Photos must be 200MB or smaller after processing.'
        : 'Videos must be 1GB or smaller.',
    );
  }
}

function normalizeGarageCommentMediaSize(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0;
}

function validateGarageCommentMedia(
  asset: ImagePickerAsset,
  kind: CommentUploadMediaKind,
) {
  const contentType = getGarageCommentContentType(asset, kind);
  const sizeBytes = normalizeGarageCommentMediaSize(asset.fileSize);
  const durationMillis =
    kind === 'video' ? normalizeGarageCommentVideoDuration(asset) : null;

  if (!contentType.startsWith(`${kind}/`)) {
    throw new Error(`Selected file must be a ${kind}.`);
  }

  if (
    kind === 'video' &&
    durationMillis != null &&
    durationMillis > MAX_VIDEO_DURATION_MILLIS
  ) {
    throw new Error('Videos must be 10 minutes or shorter.');
  }

  if (sizeBytes) {
    validateGarageCommentMediaSize(sizeBytes, kind);
  }

  return {
    contentType,
    durationMillis,
    height: normalizeGarageCommentMediaDimension(asset.height),
    sizeBytes,
    width: normalizeGarageCommentMediaDimension(asset.width),
  };
}

function buildGiphyGarageCommentMedia(gif: GiphyGif): CommentMedia {
  return {
    kind: 'gif',
    source: 'giphy',
    giphyId: gif.giphyId,
    title: gif.title,
    pageURL: gif.pageURL,
    previewURL: gif.previewURL,
    originalURL: gif.originalURL,
    width: gif.width,
    height: gif.height,
  };
}

async function uploadBlobWithProgress({
  blob,
  metadata,
  onUploadProgress,
  storageRef,
}: {
  blob: Blob;
  metadata: UploadMetadata;
  onUploadProgress?: (progress: number) => void;
  storageRef: ReturnType<typeof ref>;
}): Promise<UploadResult> {
  if (!onUploadProgress) {
    return uploadBytes(storageRef, blob, metadata);
  }

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, blob, metadata);

    task.on(
      'state_changed',
      (snapshot) => {
        const progress =
          snapshot.totalBytes > 0
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0;
        onUploadProgress(progress);
      },
      reject,
      () => {
        onUploadProgress(1);
        resolve(task.snapshot);
      },
    );
  });
}

const searchVehicleCatalogCallable = httpsCallable<
  {
    kind: 'makes' | 'models';
    make?: string;
    query?: string;
    type?: VehicleType;
    year?: number | null;
  },
  { options: VehicleCatalogOption[] }
>(functions, 'searchVehicleCatalog');
const createGarageVehicleCallable = httpsCallable<
  GarageVehicleFormInput,
  { ok: true; path: string; vehicleId: string }
>(functions, 'createGarageVehicle');
const updateGarageVehicleCallable = httpsCallable<
  GarageVehicleFormInput & { vehicleId: string },
  { ok: true; path: string; vehicleId: string }
>(functions, 'updateGarageVehicle');
const deleteGarageVehicleCallable = httpsCallable<
  { vehicleId: string },
  { ok: true }
>(functions, 'deleteGarageVehicle');
const createGarageSharePostCallable = httpsCallable<
  { vehicleId: string },
  { ok: true; postId: string }
>(functions, 'createGarageSharePost');
const setGarageVehicleReactionCallable = httpsCallable<
  { mutationId?: string; reaction: Reaction | null; vehicleId: string },
  { ok: true }
>(functions, 'setGarageVehicleReaction');
const createGarageVehicleCommentCallable = httpsCallable<
  {
    body: string;
    commentId?: string;
    media?: CommentMedia | null;
    parentCommentId?: string | null;
    replyToCommentId?: string | null;
    profileMentions?: ProfileMention[];
    vehicleId: string;
  },
  { commentId: string; ok: true }
>(functions, 'createGarageVehicleComment');
const deleteGarageVehicleCommentCallable = httpsCallable<
  { commentId: string; vehicleId: string },
  { ok: true }
>(functions, 'deleteGarageVehicleComment');
const editGarageVehicleCommentCallable = httpsCallable<
  {
    body: string;
    commentId: string;
    profileMentions: ProfileMention[];
    vehicleId: string;
  },
  { editCount: number; ok: true }
>(functions, 'editGarageVehicleComment');
const setGarageVehicleCommentReactionCallable = httpsCallable<
  {
    commentId: string;
    mutationId?: string;
    reaction: Reaction | null;
    vehicleId: string;
  },
  { ok: true }
>(functions, 'setGarageVehicleCommentReaction');
const getGarageReactionActorsCallable = httpsCallable<
  { commentId?: string; vehicleId: string },
  {
    actors: {
      createdAt: string | null;
      fullName: string;
      photoURL: string | null;
      reaction: string;
      uid: string;
      updatedAt: string | null;
    }[];
  }
>(functions, 'getGarageReactionActors');

export async function searchVehicleCatalog(input: {
  kind: 'makes' | 'models';
  make?: string;
  query?: string;
  type?: VehicleType;
  year?: number | null;
}) {
  const result = await searchVehicleCatalogCallable(input);
  return result.data.options;
}

export async function uploadGaragePhoto({
  asset,
  clientId,
  onUploadProgress,
  order,
  uid,
  vehicleId,
}: {
  asset: ImagePickerAsset;
  clientId: string;
  onUploadProgress?: (progress: number) => void;
  order: number;
  uid: string;
  vehicleId: string;
}): Promise<VehiclePhotoUpload> {
  const preparedImage = await prepareImageUpload({
    asset,
    fallbackFileName: getGarageFileName(asset),
    jpegQuality: 0.86,
    maxBytes: MAX_GARAGE_PHOTO_BYTES,
  });
  const storagePath = `vehicles/${uid}/${vehicleId}/${clientId}/${preparedImage.fileName}`;
  const storageRef = ref(storage, storagePath);
  const uploadSnapshot = await uploadBlobWithProgress({
    blob: preparedImage.blob,
    metadata: { contentType: preparedImage.contentType },
    onUploadProgress,
    storageRef,
  });

  return {
    clientId,
    contentType: preparedImage.contentType,
    downloadURL: await getDownloadURL(uploadSnapshot.ref),
    height: preparedImage.height,
    order,
    sizeBytes: preparedImage.sizeBytes,
    storagePath,
    width: preparedImage.width,
  };
}

export async function createGarageVehicle(input: GarageVehicleFormInput) {
  const result = await createGarageVehicleCallable(input);
  return result.data;
}

export async function updateGarageVehicle(
  input: GarageVehicleFormInput & { vehicleId: string },
) {
  const result = await updateGarageVehicleCallable(input);
  return result.data;
}

export async function deleteGarageVehicle(vehicleId: string) {
  const result = await deleteGarageVehicleCallable({ vehicleId });
  return result.data;
}

export async function createGarageSharePost(vehicleId: string) {
  const result = await createGarageSharePostCallable({ vehicleId });
  return result.data;
}

export async function setGarageVehicleReaction({
  mutationId,
  reaction,
  vehicleId,
}: {
  mutationId?: string;
  reaction: Reaction | null;
  vehicleId: string;
}) {
  const result = await setGarageVehicleReactionCallable({
    ...(mutationId ? { mutationId } : {}),
    reaction,
    vehicleId,
  });
  return result.data;
}

export async function createGarageVehicleComment({
  authorUid,
  body,
  giphyGif,
  mediaAsset,
  mediaKind,
  onUploadProgress,
  parentCommentId,
  replyToCommentId,
  profileMentions,
  vehicleId,
}: CreateGarageVehicleCommentInput) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );

  if (!trimmedBody && !mediaAsset && !giphyGif) {
    throw new Error('Add a comment before posting.');
  }

  if (trimmedBody.length > MAX_GARAGE_COMMENT_LENGTH) {
    throw new Error('Comments must be 1,000 characters or fewer.');
  }

  const commentId = doc(collection(db, 'vehicles', vehicleId, 'comments')).id;
  let media: CommentMedia | null = giphyGif
    ? buildGiphyGarageCommentMedia(giphyGif)
    : null;

  if (mediaAsset && mediaKind) {
    const preparedImage =
      mediaKind === 'image'
        ? await prepareImageUpload({
            asset: mediaAsset,
            fallbackFileName: getGarageCommentFileName(mediaAsset, mediaKind),
            jpegQuality: 0.86,
            maxBytes: MAX_GARAGE_PHOTO_BYTES,
          })
        : null;
    const mediaMetrics =
      mediaKind === 'video'
        ? validateGarageCommentMedia(mediaAsset, mediaKind)
        : null;
    const fileName = preparedImage
      ? preparedImage.fileName
      : getGarageCommentFileName(mediaAsset, mediaKind);
    const storagePath = `comments/${authorUid}/${vehicleId}/${commentId}/${fileName}`;
    const blob =
      preparedImage?.blob ??
      (await blobFromMediaAsset(
        mediaAsset,
        mediaKind === 'image'
          ? 'We could not read that photo. Please choose a different image.'
          : 'We could not read that video. Please choose it again and retry.',
      ));
    const contentType = preparedImage
      ? preparedImage.contentType
      : mediaMetrics!.contentType;
    const finalSizeBytes = preparedImage
      ? preparedImage.sizeBytes
      : mediaMetrics!.sizeBytes || normalizeGarageCommentMediaSize(blob.size);
    const width = preparedImage ? preparedImage.width : mediaMetrics!.width;
    const height = preparedImage ? preparedImage.height : mediaMetrics!.height;
    const durationMillis = preparedImage ? null : mediaMetrics!.durationMillis;
    const storageRef = ref(storage, storagePath);

    validateGarageCommentMediaSize(finalSizeBytes, mediaKind);

    const uploadSnapshot = await uploadBlobWithProgress({
      blob,
      metadata: { contentType },
      onUploadProgress,
      storageRef,
    });

    media = {
      kind: mediaKind,
      source: 'upload',
      storagePath,
      downloadURL: await getDownloadURL(uploadSnapshot.ref),
      contentType,
      sizeBytes: finalSizeBytes,
      width,
      height,
      durationMillis,
      thumbnail: null,
      thumbnailStatus: null,
    };
  }

  const result = await createGarageVehicleCommentCallable({
    body: trimmedBody,
    commentId,
    media,
    parentCommentId: parentCommentId ?? null,
    replyToCommentId: replyToCommentId ?? null,
    profileMentions: sanitizedMentions,
    vehicleId,
  });

  return result.data;
}

export async function editGarageVehicleComment({
  body,
  commentId,
  profileMentions,
  vehicleId,
}: {
  body: string;
  commentId: string;
  profileMentions?: ProfileMention[];
  vehicleId: string;
}) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );

  if (trimmedBody.length > MAX_GARAGE_COMMENT_LENGTH) {
    throw new Error('Comments must be 1,000 characters or fewer.');
  }

  const result = await editGarageVehicleCommentCallable({
    body: trimmedBody,
    commentId,
    profileMentions: sanitizedMentions,
    vehicleId,
  });

  return result.data;
}

export async function loadGarageVehicleCommentEditHistory({
  commentId,
  vehicleId,
}: {
  commentId: string;
  vehicleId: string;
}) {
  const historyQuery = query(
    collection(db, 'vehicles', vehicleId, 'comments', commentId, 'editHistory'),
    orderBy('editNumber', 'desc'),
    limit(50),
  );
  const snapshot = await getDocs(historyQuery);

  return snapshot.docs.map((historySnapshot) =>
    parseGarageCommentEditHistory(historySnapshot.id, historySnapshot.data()),
  );
}

export async function deleteGarageVehicleComment({
  commentId,
  vehicleId,
}: {
  commentId: string;
  vehicleId: string;
}) {
  const result = await deleteGarageVehicleCommentCallable({
    commentId,
    vehicleId,
  });
  return result.data;
}

export async function setGarageVehicleCommentReaction({
  commentId,
  mutationId,
  reaction,
  vehicleId,
}: {
  commentId: string;
  mutationId?: string;
  reaction: Reaction | null;
  vehicleId: string;
}) {
  const result = await setGarageVehicleCommentReactionCallable({
    commentId,
    ...(mutationId ? { mutationId } : {}),
    reaction,
    vehicleId,
  });
  return result.data;
}

export function subscribeToUserGarageVehicles({
  limitCount = 20,
  onError,
  onNext,
  uid,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (vehicles: GarageVehicle[]) => void;
  uid: string;
}) {
  const vehiclesQuery = query(
    collection(db, 'vehicles'),
    where('ownerUid', '==', uid),
    orderBy('updatedAt', 'desc'),
    limit(limitCount),
  );

  return onSnapshot(
    vehiclesQuery,
    (snapshot) => onNext(parseGarageVehicleSnapshot(snapshot)),
    onError,
  );
}

export function subscribeToGarageVehicle({
  onError,
  onNext,
  vehicleId,
}: {
  onError: (error: Error) => void;
  onNext: (vehicle: GarageVehicle | null) => void;
  vehicleId: string;
}) {
  return onSnapshot(
    doc(db, 'vehicles', vehicleId),
    (snapshot) =>
      onNext(
        snapshot.exists()
          ? parseGarageVehicle(snapshot.id, snapshot.data())
          : null,
      ),
    onError,
  );
}

export async function getGarageVehicle(vehicleId: string) {
  const snapshot = await getDoc(doc(db, 'vehicles', vehicleId));

  return snapshot.exists()
    ? parseGarageVehicle(snapshot.id, snapshot.data())
    : null;
}

export async function getGarageVehiclePhotos(vehicleId: string) {
  const photosQuery = query(
    collection(db, 'vehicles', vehicleId, 'photos'),
    orderBy('order', 'asc'),
    limit(GARAGE_PHOTO_LIMIT),
  );
  const snapshot = await getDocs(photosQuery);

  return snapshot.docs
    .map((photoSnapshot) =>
      parseGaragePhoto(photoSnapshot.id, photoSnapshot.data()),
    )
    .filter((photo): photo is GarageVehiclePhoto => photo !== null);
}

export function subscribeToGarageVehiclePhotos({
  onError,
  onNext,
  vehicleId,
}: {
  onError: (error: Error) => void;
  onNext: (photos: GarageVehiclePhoto[]) => void;
  vehicleId: string;
}) {
  const photosQuery = query(
    collection(db, 'vehicles', vehicleId, 'photos'),
    orderBy('order', 'asc'),
    limit(GARAGE_PHOTO_LIMIT),
  );

  return onSnapshot(
    photosQuery,
    (snapshot) =>
      onNext(
        snapshot.docs
          .map((photoSnapshot) =>
            parseGaragePhoto(photoSnapshot.id, photoSnapshot.data()),
          )
          .filter((photo): photo is GarageVehiclePhoto => photo !== null),
      ),
    onError,
  );
}

export async function getGarageVehicleComments({
  limitCount = 100,
  vehicleId,
}: {
  limitCount?: number;
  vehicleId: string;
}) {
  const commentsQuery = query(
    collection(db, 'vehicles', vehicleId, 'comments'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  );
  const snapshot = await getDocs(commentsQuery);

  return parseGarageCommentSnapshot(snapshot);
}

export function subscribeToGarageVehicleComments({
  limitCount = 100,
  onError,
  onNext,
  vehicleId,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (comments: GarageVehicleComment[]) => void;
  vehicleId: string;
}) {
  const commentsQuery = query(
    collection(db, 'vehicles', vehicleId, 'comments'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  );

  return onSnapshot(
    commentsQuery,
    (snapshot) => onNext(parseGarageCommentSnapshot(snapshot)),
    onError,
  );
}

export function subscribeToGarageVehicleReaction({
  onError,
  onNext,
  uid,
  vehicleId,
}: {
  onError: (error: Error) => void;
  onNext: (reaction: Reaction | null, mutationId: string | null) => void;
  uid: string;
  vehicleId: string;
}) {
  return onSnapshot(
    doc(db, 'vehicles', vehicleId, 'reactions', uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        onNext(null, null);
        return;
      }

      const data = snapshot.data();
      const reaction = data.reaction;
      const mutationId =
        typeof data.mutationId === 'string' && data.mutationId
          ? data.mutationId
          : null;
      onNext(isReaction(reaction) ? reaction : null, mutationId);
    },
    onError,
  );
}

export function subscribeToGarageVehicleCommentReaction({
  commentId,
  onError,
  onNext,
  uid,
  vehicleId,
}: {
  commentId: string;
  onError: (error: Error) => void;
  onNext: (reaction: Reaction | null, mutationId: string | null) => void;
  uid: string;
  vehicleId: string;
}) {
  return onSnapshot(
    doc(db, 'vehicles', vehicleId, 'comments', commentId, 'reactions', uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        onNext(null, null);
        return;
      }

      const data = snapshot.data();
      const reaction = data.reaction;
      const mutationId =
        typeof data.mutationId === 'string' && data.mutationId
          ? data.mutationId
          : null;
      onNext(isReaction(reaction) ? reaction : null, mutationId);
    },
    onError,
  );
}

async function resolveReactionActors(snapshot: QuerySnapshot) {
  const actors = await Promise.all(
    snapshot.docs.map(async (reactionSnapshot) => {
      const data = reactionSnapshot.data();
      const reaction = data.reaction;

      if (typeof reaction !== 'string') {
        return null;
      }

      const uid =
        typeof data.uid === 'string' && data.uid.length > 0
          ? data.uid
          : reactionSnapshot.id;
      const hasEmbeddedActor =
        Object.prototype.hasOwnProperty.call(data, 'actorFullName') ||
        Object.prototype.hasOwnProperty.call(data, 'actorPhotoURL');
      let fullName =
        typeof data.actorFullName === 'string' && data.actorFullName.length > 0
          ? data.actorFullName
          : 'Frienzzi user';
      let photoURL =
        typeof data.actorPhotoURL === 'string' && data.actorPhotoURL.length > 0
          ? data.actorPhotoURL
          : null;

      if (!hasEmbeddedActor) {
        try {
          const publicProfile = await getPublicProfile(uid);
          fullName = publicProfile?.fullName || fullName;
          photoURL = getProfileAvatarURL(publicProfile, 'thumb');
        } catch {
          // A missing public profile should not hide the reaction row.
        }
      }

      return {
        uid,
        reaction: reaction as Reaction,
        fullName,
        photoURL,
        createdAt: (data.createdAt as FirestoreDate) ?? null,
        updatedAt: (data.updatedAt as FirestoreDate) ?? null,
      };
    }),
  );

  return actors.filter((actor): actor is ReactionActor => actor !== null);
}

async function loadGarageReactionActorsFallback({
  commentId,
  vehicleId,
}: {
  commentId?: string;
  vehicleId: string;
}): Promise<ReactionActor[]> {
  const result = await getGarageReactionActorsCallable({
    commentId,
    vehicleId,
  });

  return result.data.actors.reduce<ReactionActor[]>((actors, actor) => {
    if (!isReaction(actor.reaction) || !actor.uid) {
      return actors;
    }

    actors.push({
      uid: actor.uid,
      reaction: actor.reaction,
      fullName: actor.fullName || 'Frienzzi user',
      photoURL: actor.photoURL,
      createdAt: null,
      updatedAt: null,
    });

    return actors;
  }, []);
}

function subscribeToReactionActorQuery({
  fallback,
  onError,
  onNext,
  reactionsRef,
}: {
  fallback?: () => Promise<ReactionActor[]>;
  onError: (error: Error) => void;
  onNext: (actors: ReactionActor[]) => void;
  reactionsRef: CollectionReference<DocumentData>;
}) {
  let active = true;
  const reactionsQuery = query(
    reactionsRef,
    orderBy('updatedAt', 'desc'),
    limit(50),
  );
  const handleFailure = (error: Error) => {
    if (!fallback) {
      onError(error);
      return;
    }

    void fallback()
      .then((actors) => {
        if (active) {
          onNext(actors);
        }
      })
      .catch(() => {
        if (active) {
          onError(error);
        }
      });
  };

  const unsubscribe = onSnapshot(
    reactionsQuery,
    (snapshot) => {
      void resolveReactionActors(snapshot)
        .then((actors) => {
          if (active) {
            onNext(actors);
          }
        })
        .catch((error: Error) => {
          if (active) {
            handleFailure(error);
          }
        });
    },
    handleFailure,
  );

  return () => {
    active = false;
    unsubscribe();
  };
}

export function subscribeToVehicleReactionActors({
  onError,
  onNext,
  vehicleId,
}: {
  onError: (error: Error) => void;
  onNext: (actors: ReactionActor[]) => void;
  vehicleId: string;
}) {
  return subscribeToReactionActorQuery({
    fallback: () => loadGarageReactionActorsFallback({ vehicleId }),
    onError,
    onNext,
    reactionsRef: collection(db, 'vehicles', vehicleId, 'reactions'),
  });
}

export function subscribeToVehicleCommentReactionActors({
  commentId,
  onError,
  onNext,
  vehicleId,
}: {
  commentId: string;
  onError: (error: Error) => void;
  onNext: (actors: ReactionActor[]) => void;
  vehicleId: string;
}) {
  return subscribeToReactionActorQuery({
    fallback: () =>
      loadGarageReactionActorsFallback({
        commentId,
        vehicleId,
      }),
    onError,
    onNext,
    reactionsRef: collection(
      db,
      'vehicles',
      vehicleId,
      'comments',
      commentId,
      'reactions',
    ),
  });
}

export async function getPublicGarageVehicle({
  includeComments = true,
  username,
  vehicleSlug,
}: {
  includeComments?: boolean;
  username: string;
  vehicleSlug: string;
}) {
  const searchParams = includeComments ? '' : '?comments=0';
  const { data, response } = await fetchPublicJson<{
    comments: GarageVehicleComment[];
    ownerProfile: unknown | null;
    photos: GarageVehiclePhoto[];
    vehicle: GarageVehicle | null;
  }>(
    `/api/public/garage/${encodeURIComponent(username)}/${encodeURIComponent(
      vehicleSlug,
    )}${searchParams}`,
  );

  if (!response.ok && response.status !== 404) {
    throw new Error('Unable to load this vehicle.');
  }

  const vehicle = data.vehicle
    ? parseGarageVehicle(
        data.vehicle.id,
        data.vehicle as unknown as Record<string, unknown>,
      )
    : null;
  const ownerProfile =
    data.ownerProfile && typeof data.ownerProfile === 'object'
      ? (data.ownerProfile as Record<string, unknown>)
      : null;
  const vehicleWithLiveOwnerProfile = withLiveGarageVehicleOwnerProfile(
    vehicle,
    ownerProfile,
  );

  return {
    comments: Array.isArray(data.comments)
      ? sortGarageCommentsNewestFirst(
          data.comments.map((comment) =>
            parseGarageComment(
              comment.id,
              comment as unknown as Record<string, unknown>,
            ),
          ),
        )
      : [],
    photos: Array.isArray(data.photos)
      ? data.photos
          .map((photo) =>
            parseGaragePhoto(
              photo.id,
              photo as unknown as Record<string, unknown>,
            ),
          )
          .filter((photo): photo is GarageVehiclePhoto => photo !== null)
      : [],
    vehicle: vehicleWithLiveOwnerProfile,
  };
}

export function garageVehicleTitle(
  vehicle: Pick<GarageVehicle, 'make' | 'model' | 'nickname' | 'year'>,
) {
  const makeModel = [vehicle.year, vehicle.make, vehicle.model]
    .filter(Boolean)
    .join(' ');

  return vehicle.nickname ? `${vehicle.nickname} · ${makeModel}` : makeModel;
}

export function garageVehiclePath(
  vehicle: Pick<GarageVehicle, 'ownerUsername' | 'slug'>,
) {
  return vehicle.ownerUsername
    ? `/garage/${vehicle.ownerUsername}/${vehicle.slug}`
    : null;
}

export function garageVehicleShareURL(
  vehicle: Pick<GarageVehicle, 'ownerUsername' | 'slug'>,
) {
  const path = garageVehiclePath(vehicle);

  return path ? `https://frienzzi.com${path}` : null;
}

export function newGarageVehicleId() {
  return doc(collection(db, 'vehicles')).id;
}
